import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import FactoryIcon from '@mui/icons-material/Factory'
import type { Supplier, SupplierInput, SupplierQuery } from '@iobiz/shared'
import { supplierInputSchema } from '@iobiz/shared'
import { ApiError } from '../api/client.js'
import { DataTable, EmptyState, type Column } from '../ui/DataTable.js'
import { ActionsDOnglet } from '../ui/ActionsDOnglet.js'
import { useSuppliers } from '../data/hooks.js'
import { saveSupplier } from '../data/repositories/catalog.write.js'
import { useSessionStore } from '../stores/sessionStore.js'

/**
 * Fournisseurs (W-14).
 *
 * Au Sprint 7, cette fiche portera les commandes et les réceptions ; pour
 * l'instant elle reste un annuaire, mais le compteur d'articles fournis
 * montre déjà le lien avec le catalogue.
 */

const EMPTY: Record<string, string> = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  website: '',
  addressLine1: '',
  postalCode: '',
  city: '',
  countryCode: 'CM',
  taxId: '',
  paymentTermsDays: '0',
  notes: '',
}

function SupplierFormDialog({
  open,
  storeId,
  supplier,
  onClose,
  onSaved,
}: {
  open: boolean
  storeId: string
  supplier?: Supplier | null
  onClose: () => void
  /** Reçoit la réponse du serveur : elle porte la nouvelle version, requise
   *  par le prochain If-Match. */
  onSaved: (supplier: Supplier) => void
}): JSX.Element {
  const [form, setForm] = useState(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setErrors({})
    setBanner(null)
    setForm(
      supplier
        ? {
            companyName: supplier.companyName,
            contactName: supplier.contactName ?? '',
            email: supplier.email ?? '',
            phone: supplier.phone ?? '',
            website: supplier.website ?? '',
            addressLine1: supplier.addressLine1 ?? '',
            postalCode: supplier.postalCode ?? '',
            city: supplier.city ?? '',
            countryCode: supplier.countryCode ?? 'CM',
            taxId: supplier.taxId ?? '',
            paymentTermsDays: String(supplier.paymentTermsDays),
            notes: supplier.notes ?? '',
          }
        : EMPTY,
    )
  }, [open, supplier])

  const set = (k: string, v: string): void => {
    setForm((f) => ({ ...f, [k]: v }))
    if (errors[k]) setErrors(({ [k]: _r, ...rest }) => rest)
  }

  async function submit(): Promise<void> {
    const parsed = supplierInputSchema.safeParse({
      ...form,
      paymentTermsDays: Number(form['paymentTermsDays'] || 0),
    })
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path.join('.') || 'form'] ??= issue.message
      }
      setErrors(fieldErrors)
      return
    }

    setSaving(true)
    try {
      const input = parsed.data as SupplierInput
      const saved = supplier
        ? await saveSupplier(storeId, input, supplier)
        : await saveSupplier(storeId, input)
      onSaved(saved)
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        setBanner(err.message)
        setErrors(err.fieldErrors())
      } else setBanner('Enregistrement impossible.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{supplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</DialogTitle>
      <DialogContent dividers>
        {banner && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {banner}
          </Alert>
        )}
        <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            label="Raison sociale"
            required
            value={form['companyName']}
            onChange={(e) => set('companyName', e.target.value)}
            error={Boolean(errors['companyName'])}
            helperText={errors['companyName']}
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label="Contact"
            value={form['contactName']}
            onChange={(e) => set('contactName', e.target.value)}
          />
          <TextField
            label="Téléphone"
            value={form['phone']}
            onChange={(e) => set('phone', e.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            value={form['email']}
            onChange={(e) => set('email', e.target.value)}
            error={Boolean(errors['email'])}
            helperText={errors['email']}
          />
          <TextField
            label="Site Internet"
            value={form['website']}
            onChange={(e) => set('website', e.target.value)}
          />
          <TextField
            label="Adresse"
            value={form['addressLine1']}
            onChange={(e) => set('addressLine1', e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label="Ville"
            value={form['city']}
            onChange={(e) => set('city', e.target.value)}
          />
          <TextField
            label="N° fiscal"
            value={form['taxId']}
            onChange={(e) => set('taxId', e.target.value)}
          />
          <TextField
            label="Délai de paiement (jours)"
            type="number"
            value={form['paymentTermsDays']}
            onChange={(e) => set('paymentTermsDays', e.target.value)}
          />
          <TextField
            label="Notes"
            multiline
            minRows={2}
            value={form['notes']}
            onChange={(e) => set('notes', e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving}>
          {saving ? 'Enregistrement…' : supplier ? 'Enregistrer' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function SupplierListPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const [params, setParams] = useSearchParams()
  const query: Partial<SupplierQuery> = useMemo(
    () => ({
      q: params.get('q') ?? undefined,
      sort: (params.get('sort') as SupplierQuery['sort']) ?? 'companyName',
      limit: Number(params.get('limit') ?? 50),
      offset: Number(params.get('offset') ?? 0),
    }),
    [params],
  )

  const page = useSuppliers(store?.storeId, query)
  const loading = page === undefined
  const [draft, setDraft] = useState(query.q ?? '')
  const [editing, setEditing] = useState<Supplier | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const patch = useCallback(
    (changes: Record<string, string | undefined>) => {
      const next = new URLSearchParams(params)
      for (const [k, v] of Object.entries(changes)) {
        if (v === undefined || v === '') next.delete(k)
        else next.set(k, v)
      }
      if (!('offset' in changes)) next.delete('offset')
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (draft !== (query.q ?? '')) patch({ q: draft })
    }, 300)
    return () => clearTimeout(timer)
  }, [draft, query.q, patch])

  const columns: Column<Supplier>[] = [
    {
      key: 'name',
      label: 'Société',
      sortKey: 'companyName',
      render: (s) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'secondary.main' }}>
            {s.companyName.slice(0, 2).toUpperCase()}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {s.companyName}
          </Typography>
        </Box>
      ),
    },
    { key: 'contact', label: 'Contact', render: (s) => s.contactName ?? '—' },
    { key: 'phone', label: 'Téléphone', numeric: true, render: (s) => s.phone ?? '—' },
    { key: 'email', label: 'Email', render: (s) => s.email ?? '—' },
    { key: 'city', label: 'Ville', render: (s) => s.city ?? '—' },
    {
      key: 'items',
      label: 'Articles fournis',
      align: 'right',
      numeric: true,
      render: (s) => s.itemCount,
    },
    {
      key: 'terms',
      label: 'Paiement',
      render: (s) => (s.paymentTermsDays > 0 ? `${s.paymentTermsDays} j` : 'Comptant'),
    },
  ]

  return (
    <>
      {/* ⭐ L'action rejoint la barre d'onglets. Le compte disparaît avec le
          titre : la pagination du tableau l'annonce déjà, en bas. */}
      <ActionsDOnglet>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null)
            setFormOpen(true)
          }}
        >
          Nouveau fournisseur
        </Button>
      </ActionsDOnglet>

      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <TextField
          placeholder="Société, contact, e-mail, ville…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          size="small"
          sx={{ maxWidth: 420, width: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Alert severity="info" variant="outlined" sx={{ mb: 4 }}>
        Les commandes fournisseurs et les réceptions de marchandise arrivent au Sprint 7.
      </Alert>

      <DataTable
        columns={columns}
        rows={page?.data ?? []}
        rowKey={(s) => s.id}
        loading={loading}
        total={page?.total ?? 0}
        limit={query.limit ?? 50}
        offset={query.offset ?? 0}
        sort={query.sort}
        onSortChange={(sort) => patch({ sort })}
        onPageChange={(offset, limit) => patch({ offset: String(offset), limit: String(limit) })}
        onRowClick={(s) => navigate(`/s/${slug}/catalogue/fournisseurs/${s.id}`)}
        emptyState={
          <EmptyState
            icon={<FactoryIcon sx={{ fontSize: 48 }} />}
            title={query.q ? 'Aucun résultat' : 'Aucun fournisseur'}
            description={
              query.q
                ? 'Modifiez vos critères de recherche.'
                : 'Ajoutez vos fournisseurs pour les rattacher à vos articles.'
            }
            action={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                Nouveau fournisseur
              </Button>
            }
          />
        }
      />

      {store && (
        <SupplierFormDialog
          open={formOpen}
          storeId={store.storeId}
          supplier={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => undefined}
        />
      )}
    </>
  )
}

export { SupplierFormDialog }
export const SupplierWebsiteLink = ({ url }: { url: string }): JSX.Element => (
  <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
    {url}
  </Link>
)
